<template>
  <div class="searchorders">
    <h1 class="subheading grey--text">Cart Editor</h1>

    <v-container class="my-1">
      <v-row>
        <v-col>
          <v-text-field
            v-model="cartId"
            label="CartId"
            prepend-icon="shopping_cart"
            clearable
            :rules="inputRules"
          >
            <template v-slot:append>
              <v-btn
                class="success mx-1 mt-3"
                @click="startSearch"
                :loading="loading"
                :disabled="searchDisabled"
                ><v-icon>search</v-icon><span>Search for cart</span></v-btn
              >
              <v-btn icon class="success mx-1 mt-3" @click="insertStoredCartId"
                ><v-icon>add</v-icon></v-btn
              >
            </template></v-text-field
          >
        </v-col>
      </v-row>

      <v-card v-if="currentPayment.cartId">
        <v-card-text class="grey--text">
          <v-row>
            <v-col cols="4 packed">
              <div v-if="currentPayment.email" class="font-weight-bold">
                Email: {{ currentPayment.email }}
                <span class="caption"
                  ><v-icon
                    size="15"
                    v-on:click.stop="copyEmailToClipboard(currentPayment.email)"
                    >content_copy</v-icon
                  ></span
                >
              </div>
            </v-col>
            <v-col cols="4 packed">
              <div class="ma-0">Status: {{ currentPayment.status }}</div>
            </v-col>
            <v-col cols="4 packed">
              <div v-if="currentPayment.notes" class="font-weight-bold">
                Notes: {{ currentPayment.notes }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4 packed">
              <div>Order Date: {{ currentPayment.orderDate }}</div>
            </v-col>
            <v-col cols="4 packed">
              <div>Confirm Date: {{ currentPayment.confirmDate }}</div>
            </v-col>
            <v-col cols="4 packed">
              <div v-if="currentPayment.sentDate">
                Sent Date: {{ currentPayment.sentDate }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4 packed">
              <div>Cart Net Value: {{ currentPayment.netCost.price }}</div>
            </v-col>
            <v-col cols="4 packed">
              <div>Cart Total Value: {{ currentPayment.totalCost.price }}</div>
            </v-col>
            <v-col cols="4 packed">
              <div>Shipping Value: {{ currentPayment.shipping.price }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12 packed">
              <div>
                Address: {{ currentPayment.customerAddress }}
                <span class="caption"
                  ><v-icon
                    size="15"
                    @click="
                      copyAddressToClipboard(currentPayment.customerAddress)
                    "
                    >content_copy</v-icon
                  ></span
                >
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon color="red" v-on="on" @click="deletePayentAndCart()">
                <v-icon>remove_circle</v-icon>
              </v-btn>
            </template>
            <span>Delete Payment and cart</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon color="red" v-on="on" @click="cancelOrderAndNotify()">
                <v-icon>cancel_schedule_send</v-icon>
              </v-btn>
            </template>
            <span>Cancel and notify</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon color="red" v-on="on" @click="cancelOrderSilent()">
                <v-icon>delete_forever</v-icon>
              </v-btn>
            </template>
            <span>Cancel silent</span>
          </v-tooltip>
          <v-tooltip top v-if="currentPayment.status === 'NEW'">
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                color="primary"
                v-on="on"
                :loading="resending"
                @click="resendOrderEmail()"
              >
                <v-icon>forward_to_inbox</v-icon>
              </v-btn>
            </template>
            <span>Resend order confirmation email</span>
          </v-tooltip>
          <UpdateNoteDialog
            v-bind:cartId="cartId"
            v-bind:notes="currentPayment.notes"
            @noteChanged="startSearch"
            @click="setCurrentNotes()"
          />
          <UpdateNoteToCustomerDialog
            v-bind:cartId="cartId"
            v-bind:notesToCustomer="currentPayment.notesToCustomer"
            @noteChanged="startSearch"
            @click="setCurrentNotes()"
          />
          <UpdateAddressDialog
            v-bind:cartId="cartId"
            @addressChanged="startSearch"
          />
          <UpdateEmailDialog
            v-bind:cartId="cartId"
            v-bind:email="currentPayment.email"
            @emailChanged="startSearch"
          />
          <AddItemDialog v-bind:cartId="cartId" v-bind:currency="cartCurrency" @itemAdded="startSearch" />
          <UpdateStatusDialog
            v-bind:cartId="cartId"
            @statusChanged="startSearch"
          />
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-on="on"
                color="green"
                @click="recalculatePaymentTotal()"
              >
                <v-icon>refresh</v-icon>
              </v-btn>
            </template>
            <span>Recalculate cart price</span>
          </v-tooltip>
          <GetInvitationDialog v-bind:cartId="cartId" v-bind:icon="true" />
        </v-card-actions>
      </v-card>

      <v-card
        v-for="cartitem in this.cartContents"
        :key="cartitem.id"
        class="pt-1 px-4 my-1"
      >
        <v-card-text class="py-0 px-4 my-1">
          {{ cartitem.item }} - {{ cartitem.description }} ({{
            cartitem.price
          }})
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon color="red" v-on="on" @click="deleteItem(cartitem)">
                <v-icon>block</v-icon>
              </v-btn>
            </template>
            <span>Delete item</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-on="on"
                color="green"
                @click="duplicateItem(cartitem)"
              >
                <v-icon>dynamic_feed</v-icon>
              </v-btn>
            </template>
            <span>Duplicate</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import cartApi from "../modules/cart-api";
import AddItemDialog from "../components/AddItemDialog";
import UpdateEmailDialog from "../components/UpdateEmailDialog";
import UpdateNoteDialog from "../components/UpdateNoteDialog";
import UpdateNoteToCustomerDialog from "../components/UpdateNoteToCustomerDialog";
import UpdateAddressDialog from "../components/UpdateAddressDialog";
import GetInvitationDialog from "../components/GetInvitationDialog";
import UpdateStatusDialog from "../components/UpdateStatusDialog";
import { store } from "../store.js";

export default {
  components: {
    UpdateNoteDialog,
    UpdateNoteToCustomerDialog,
    AddItemDialog,
    UpdateEmailDialog,
    GetInvitationDialog,
    UpdateAddressDialog,
    UpdateStatusDialog,
  },
  data() {
    return {
      currentPayment: {},
      cartId: "",
      cartContents: [],
      inputRules: [
        (v) => !!v || "This field is required",
        (v) => v.length === 36 || "CartId must be 36 characters",
      ],
      loading: false,
      resending: false,
    };
  },
  created() {
    this.searchForCartIdIfOnClipboard();
  },
  methods: {
    async searchForCartIdIfOnClipboard() {
      let clipVal = store.getCartId();
      if (clipVal.length == 36) {
        this.cartId = clipVal;
        this.startSearch();
      }
    },
    async startSearch() {
      this.loading = true;
      try {
        let cartPayment = await cartApi.getPaymentForCart(this.cartId);
        if (cartPayment && cartPayment.cartId) {
          this.currentPayment = cartPayment;
          let res = await cartApi.getCart(this.cartId);
          if (res.cart || res.cart.length > 0) {
            this.cartContents = res.cart;
          } else {
            this.$toast("No cart found!");
          }
        } else {
          this.$toast("No payment found!");
        }
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.$toast("Error performing search: " + e);
      }
    },
    async clearCart() {
      this.currentPayment = {};
      this.cartContents = [];
      this.cartId = "";
      store.storeCartId("");
    },
    async cancelOrderSilent() {
      await cartApi.cancelOrderSilent(this.cartId);
    },
    async deletePayentAndCart() {
      await cartApi.deletePayment(this.currentPayment.id);
      await cartApi.deleteCartAdmin(this.cartId);
      this.startSearch();
      this.$toast("Payment and cart deleted");
    },
    async cancelOrderAndNotify() {
      await cartApi.cancelOrderAndNotify(this.cartId);
    },
    async resendOrderEmail() {
      this.resending = true;
      try {
        await cartApi.resendSubmitOrder(this.cartId);
        this.$toast("Order confirmation email resent");
      } catch (e) {
        this.$toast("Error resending order email: " + e);
      }
      this.resending = false;
    },
    async deleteItem(itemToDelete) {
      await cartApi.deleteItemFromCartAdmin(this.cartId, itemToDelete.id);
      this.startSearch();
      this.$toast("Item deleted");
    },
    async duplicateItem(itemToCopy) {
      await cartApi.postAddItemToCartAdmin(
        this.cartId,
        itemToCopy.item,
        1,
        itemToCopy.price,
        itemToCopy.currency
      );
      this.startSearch();
      this.$toast("Item duplicated");
    },
    async recalculatePaymentTotal() {
      await cartApi.postRecalculatePaymentTotal(this.cartId);
      this.startSearch();
    },
    insertStoredCartId() {
      let tmpCartId = store.getCartId();
      if (tmpCartId) {
        this.cartId = tmpCartId;
        this.startSearch();
      }
    },
    async copyEmailToClipboard(email) {
      let copyRes = false;
      await navigator.clipboard.writeText(email).then(
        function () {
          copyRes = true;
        },
        function (err) {
          console.error("Async: Could not copy text: ", err);
        }
      );
      if (copyRes) this.$toast("Copied email " + email);
    },
    async copyAddressToClipboard(address) {
      let copyRes = false;
      await navigator.clipboard.writeText(address).then(
        function () {
          copyRes = true;
        },
        function (err) {
          console.error("Async: Could not copy text: ", err);
        }
      );
      if (copyRes) this.$toast("Copied address");
    },
  },
  computed: {
    cartCurrency() {
      if (this.cartContents && this.cartContents.length > 0) {
        return this.cartContents[0].currency || 'usd';
      }
      return 'usd';
    },
    searchDisabled() {
      if (!this.cartId || this.cartId === undefined) {
        return true;
      } else {
        return this.cartId.length !== 36;
      }
    },
  },
};
</script>

<style>
.packed {
  /*  color: red; */
  padding-top: 0px;
  padding-bottom: 0px;
}
</style>